import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import apiClient, { setUnauthorizedLogoutHandler } from '../api/api';
import toast from 'react-hot-toast';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const normalizeToken = (token) => {
  if (!token || token === 'null' || token === 'undefined') return null;
  const sanitized = String(token).trim().replace(/^"|"$/g, '');
  if (!sanitized || sanitized === 'null' || sanitized === 'undefined') return null;
  if (sanitized.toLowerCase().startsWith('bearer ')) {
    return sanitized.slice(7).trim() || null;
  }
  return sanitized;
};

const applyToken = (token) => {
  const normalized = normalizeToken(token);
  if (!normalized) return null;
  localStorage.setItem('token', normalized);
  apiClient.defaults.headers.common['Authorization'] = `Bearer ${normalized}`;
  return normalized;
};

/**
 * useAuthStore: Consolidación de autenticación y sesión
 * Absorbe: userStore.js + passStore.js + authStore.js
 * 
 * Responsabilidad: Gestionar el estado del usuario actualmente logueado, su sesión, token,
 * y acciones relacionadas con autenticación (login, logout, registro).
 * 
 * También gestiona:
 * - myPasses: Pases de creador que el usuario ha comprado
 * - Acciones: fetchMyPasses(), purchasePass()
 */

const initialState = {
  // --- AUTH STATE ---
  isLoggedIn: false,
  currentUser: null,
  user: null,
  token: null,
  isLoading: true,
  isVerifying: true, // 🔴 NUEVO: Estado inicial de verificación
  status: 'unauthenticated', // 'loading' | 'authenticated' | 'unauthenticated'
  error: null,

  // --- PASSES STATE (from passStore) ---
  myPasses: [],
  loading: false, // compat: passStore.loading
  purchaseInProgress: false, // compat: passStore.purchaseInProgress
  passError: null,
};

export const useAuthStore = create(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ============================================
        // --- AUTHENTICATION ACTIONS ---
        // ============================================

        /**
         * Login: Inicia sesión del usuario
         */
        login: async (credentials) => {
          set({ status: 'loading', isLoading: true });
          try {
            let response;
            let lastError;

            for (let attempt = 1; attempt <= 3; attempt += 1) {
              try {
                response = await apiClient.post('/auth/login', credentials);
                break;
              } catch (error) {
                lastError = error;
                const status = error?.response?.status;
                const code = error?.response?.data?.code;
                const isWarmupError = status === 503 || code === 'DB_NOT_READY';
                if (!isWarmupError || attempt === 3) {
                  throw error;
                }
                await wait(1200 * attempt);
              }
            }

            if (!response) {
              throw lastError || new Error('No se pudo completar el login');
            }

            const { token, user } = response.data;

            // Normalización de roles
            let derivedRole = user.role;
            const roles = user.roles || [];
            if (roles.includes('admin_root')) derivedRole = 'admin_root';
            else if (roles.length > 0 && !derivedRole) derivedRole = roles[0];

            const userWithUnderscoreId = {
              ...user,
              _id: user.id || user._id,
              role: derivedRole,
              walletBalance: user.walletBalance ?? user.coins ?? 0,
            };

            // Guardar token en localStorage y en state
            const normalizedToken = applyToken(token);
            set((state) => ({
              isLoggedIn: true,
              currentUser: userWithUnderscoreId,
              user: userWithUnderscoreId,
              token: normalizedToken,
              isLoading: false,
              status: 'authenticated',
              error: null,
            }));

            toast.success(`¡Bienvenido, ${user.username}!`);
            return { success: true, user: userWithUnderscoreId };
          } catch (error) {
            const status = error?.response?.status;
            const code = error?.response?.data?.code;
            const errorMessage = (status === 503 || code === 'DB_NOT_READY')
              ? 'El servidor está iniciando, inténtalo de nuevo en unos segundos.'
              : (error.response?.data?.message || 'Error al iniciar sesión');
            set((state) => ({
              isLoggedIn: false,
              currentUser: null,
              user: null,
              token: null,
              isLoading: false,
              status: 'unauthenticated',
              error: errorMessage,
            }));
            toast.error(errorMessage);
            return { success: false };
          }
        },

        /**
         * Register: Registra un nuevo usuario
         */
        register: async (userData) => {
          set({ status: 'loading', isLoading: true });
          try {
            const response = await apiClient.post('/auth/register', userData);
            const { token, user } = response.data;

            const userWithUnderscoreId = {
              ...user,
              _id: user.id || user._id,
              role: user.role || 'user',
              walletBalance: user.walletBalance ?? user.coins ?? 0,
            };

            const normalizedToken = applyToken(token);
            set((state) => ({
              isLoggedIn: true,
              currentUser: userWithUnderscoreId,
              user: userWithUnderscoreId,
              token: normalizedToken,
              isLoading: false,
              status: 'authenticated',
              error: null,
            }));

            toast.success('¡Cuenta creada exitosamente!');
            return { success: true, user: userWithUnderscoreId };
          } catch (error) {
            const errorMessage = error.response?.data?.message || 'Error al registrarse';
            set((state) => ({
              isLoading: false,
              status: 'unauthenticated',
              error: errorMessage,
            }));
            toast.error(errorMessage);
            return { success: false };
          }
        },

        /**
         * Logout: Cierra la sesión del usuario
         */
        logout: () => {
          localStorage.removeItem('token');
          delete apiClient.defaults.headers.common['Authorization'];
          set((state) => ({
            isLoggedIn: false,
            currentUser: null,
            user: null,
            token: null,
            status: 'unauthenticated',
          }));
        },
        /**
         * CheckAuthStatus: Valida el token y obtiene datos del usuario al cargar la app
         * 🔴 NUEVO: Verificación suave con skeleton - sin errores 401 en consola
         */
        checkAuthStatus: async () => {
          const persistedToken = normalizeToken(get().token);
          const localToken = normalizeToken(localStorage.getItem('token'));
          const token = persistedToken || localToken;
          
          if (!token) {
            set((state) => ({
              isLoading: false,
              isVerifying: false,
              isLoggedIn: false,
              currentUser: null,
              user: null,
              token: null,
              status: 'unauthenticated',
            }));
            return;
          }

          set({ status: 'loading', isLoading: true, isVerifying: true });
          try {
            const normalizedToken = applyToken(token);
            const response = await apiClient.get('/auth/me');
            const user = response.data;

            const userWithUnderscoreId = {
              ...user,
              _id: user.id || user._id,
              walletBalance: user.walletBalance ?? user.coins ?? 0,
            };

            set((state) => ({
              isLoggedIn: true,
              currentUser: userWithUnderscoreId,
              user: userWithUnderscoreId,
              token: normalizedToken,
              isLoading: false,
              isVerifying: false,
              status: 'authenticated',
              error: null,
            }));
          } catch (error) {
            localStorage.removeItem('token');
            delete apiClient.defaults.headers.common['Authorization'];
            set((state) => ({
              isLoading: false,
              isVerifying: false,
              isLoggedIn: false,
              currentUser: null,
              user: null,
              token: null,
              status: 'unauthenticated',
              error: null, // 🔴 NO mostrar error - silencio total
            }));
          }
        },

        /**
         * UpdatePassword: Cambia la contraseña del usuario logueado
         */
        updatePassword: async (oldPassword, newPassword) => {
          try {
            const response = await apiClient.post('/auth/update-password', {
              oldPassword,
              newPassword,
            });
            toast.success('Contraseña actualizada correctamente.');
            return { success: true };
          } catch (error) {
            const errorMessage =
              error.response?.data?.message || 'No se pudo actualizar la contraseña';
            toast.error(errorMessage);
            return { success: false };
          }
        },

        /**
         * SetCurrentUser: Actualización directa del usuario (para actualizaciones optimistas)
         */
        setCurrentUser: (user) => {
          set((state) => ({
            currentUser: user,
            user,
          }));
        },

        // Alias de compatibilidad
        setUser: (user) => {
          set((state) => ({
            currentUser: user,
            user,
          }));
        },

        /**
         * UpgradeToBlogger: Convierte al usuario actual en Blogger (from userStore)
         */
        upgradeToBlogger: async () => {
          set((state) => ({
            isLoading: true,
          }));
          try {
            const response = await apiClient.post('/users/profile/claim-blogger-rank');
            const updatedUser = response.data.user || response.data;

            set((state) => ({
              currentUser: updatedUser,
              user: updatedUser,
              isLoading: false,
            }));

            toast.success(response.data.message || 'Activacion iniciada. Te avisaremos cuando todo este listo.', {
              icon: '🎉',
              style: {
                borderRadius: '10px',
                background: '#333',
                color: '#fff',
              },
            });
          } catch (error) {
            set((state) => ({
              isLoading: false,
            }));
            toast.error(error.response?.data?.message || 'No se pudo procesar la solicitud. Inténtalo de nuevo.');
          }
        },

        // ============================================
        // --- PASSES ACTIONS (from passStore) ---
        // ============================================

        /**
         * FetchMyPasses: Obtiene los pases de creador comprados por el usuario
         */
        fetchMyPasses: async () => {
          const token = get().token || localStorage.getItem('token');
          if (!token) {
            set({ myPasses: [], loading: false, passError: null });
            return [];
          }

          set({ loading: true, passError: null });
          try {
            const response = await apiClient.get('/pass/my-passes');
            set({
              myPasses: response.data.passes || [],
              loading: false,
            });
            return response.data.passes;
          } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al obtener pases';
            set({
              passError: errorMsg,
              loading: false,
            });
            if (error.response?.status !== 401) {
              console.error('Error fetching passes:', error);
            }
            return [];
          }
        },

        /**
         * PurchasePass: Compra un pase de creador
         */
        purchasePass: async (creatorId, tier) => {
          set({ purchaseInProgress: true, passError: null });
          try {
            const response = await apiClient.post('/pass/purchase', {
              creatorId,
              tier,
              duration: 30, // 30 días por defecto
            });

            // Actualizar lista de pases
            await get().fetchMyPasses();

            set({ purchaseInProgress: false });
            return {
              success: true,
              data: response.data,
              wasFree: response.data.wasFree,
            };
          } catch (error) {
            const errorMsg =
              error.response?.data?.message || 'Error al comprar pase';
            set({
              passError: errorMsg,
              purchaseInProgress: false,
            });
            toast.error(errorMsg);
            return { success: false };
          }
        },

        /**
         * CancelPass: Cancela un pase suscrito
         */
        cancelPass: async (passId) => {
          set({ loading: true, passError: null });
          try {
            const response = await apiClient.post(`/pass/cancel/${passId}`);
            toast.success('Pase cancelado correctamente.');
            await get().fetchMyPasses();
            set({ loading: false });
            return { success: true };
          } catch (error) {
            const errorMsg =
              error.response?.data?.message || 'Error al cancelar pase';
            set({
              passError: errorMsg,
              loading: false,
            });
            toast.error(errorMsg);
            return { success: false };
          }
        },

        // Verificar acceso a creador
        checkAccess: async (creatorId) => {
          const token = get().token || localStorage.getItem('token');
          if (!token) {
            return { hasAccess: false, isOwner: false, tier: null };
          }

          try {
            const response = await apiClient.get(`/pass/check-access/${creatorId}`);
            return response.data;
          } catch (error) {
            if (error.response?.status !== 401) {
              console.error('Error checking access:', error);
            }
            return { hasAccess: false, isOwner: false, tier: null };
          }
        },

        // Verificar si tiene pase para creador específico
        hasPassFor: (creatorId) => {
          const passes = get().myPasses;
          return passes.some(
            (pass) => pass.creatorId?._id === creatorId || pass.creatorId === creatorId
          );
        },

        // Obtener tier de pase para creador
        getPassTier: (creatorId) => {
          const passes = get().myPasses;
          const pass = passes.find(
            (p) => p.creatorId?._id === creatorId || p.creatorId === creatorId
          );
          return pass?.tier || null;
        },

        // Socket: Actualizar cuando se compra un pase
        handlePassPurchased: (data) => {
          console.log('✅ Pass purchased via socket:', data);
          get().fetchMyPasses();
        },

        // Reset (compat)
        reset: () => {
          set({
            myPasses: [],
            loading: false,
            passError: null,
            purchaseInProgress: false,
          });
        },
      }),
      {
        name: 'auth-storage', // Clave en localStorage
        // Solo persistir token y datos de usuario (sin funciones)
        partialize: (state) => ({
          token: state.token,
          currentUser: state.currentUser,
          user: state.currentUser,
          isLoggedIn: state.isLoggedIn,
        }),
        onRehydrateStorage: () => (state) => {
          const token = normalizeToken(state?.token) || normalizeToken(localStorage.getItem('token'));
          if (token) {
            applyToken(token);
          }
        },
      }
    )
  )
);

setUnauthorizedLogoutHandler(() => {
  const authState = useAuthStore.getState();
  if (authState?.logout) {
    authState.logout();
  }
});
